// ABOUTME: API functions for SharePoint operations with proper error handling
// ABOUTME: Typed API calls aligned with backend endpoints and response formats

import type {
    IApiError,
    IBrowseRequest,
    IBrowseResponse,
    IGenerateNodesRequest,
    IGenerateNodesResponse,
    IItemsRequest,
    IItemsResponse,
    ISearchRequest,
    ISearchResponse,
} from './types';

/**
 * Browse SharePoint files and folders
 */
export const browserItems = async (params: IBrowseRequest): Promise<IBrowseResponse> => {
    const response = await fetch('/api/sharepoint/browse', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(params),
    });

    if (!response.ok) {
        const error: IApiError = {
            message: `HTTP ${response.status}: ${response.statusText}`,
            code: response.status.toString(),
        };
        throw new Error(error.message);
    }

    const result: IBrowseResponse = await response.json();

    if (!result.success) {
        const error: IApiError = {
            message: result.message || result.error || 'Failed to browse files',
            code: result.error,
        };
        throw new Error(error.message);
    }

    return result;
};

/**
 * Search SharePoint files
 */
export const searchItems = async (params: ISearchRequest): Promise<ISearchResponse> => {
    const response = await fetch('/api/sharepoint/search', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(params),
    });

    if (!response.ok) {
        const error: IApiError = {
            message: `HTTP ${response.status}: ${response.statusText}`,
            code: response.status.toString(),
        };
        throw new Error(error.message);
    }

    const result: ISearchResponse = await response.json();

    if (!result.success) {
        const error: IApiError = {
            message: result.message || result.error || 'Failed to search files',
            code: result.error,
        };
        throw new Error(error.message);
    }

    return result;
};

/**
 * Get multiple SharePoint items
 */
export const getItems = async (params: IItemsRequest): Promise<IItemsResponse> => {
    const response = await fetch('/api/sharepoint/items', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(params),
    });

    if (!response.ok) {
        const error: IApiError = {
            message: `HTTP ${response.status}: ${response.statusText}`,
            code: response.status.toString(),
        };
        throw new Error(error.message);
    }

    const result: IItemsResponse = await response.json();

    if (!result.success) {
        const error: IApiError = {
            message: result.message || result.error || 'Failed to fetch items',
            code: result.error,
        };
        throw new Error(error.message);
    }

    return result;
};

/**
 * Generate nodes from SharePoint items
 */
export const generateNodes = async (
    params: IGenerateNodesRequest,
): Promise<IGenerateNodesResponse> => {
    const response = await fetch('/api/sharepoint/generateNodes', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(params),
    });

    if (!response.ok) {
        const error: IApiError = {
            message: `HTTP ${response.status}: ${response.statusText}`,
            code: response.status.toString(),
        };
        throw new Error(error.message);
    }

    const result: IGenerateNodesResponse = await response.json();

    if (!result.success) {
        const error: IApiError = {
            message: result.message || result.error || 'Failed to generate nodes',
            code: result.error,
        };
        throw new Error(error.message);
    }

    return result;
};
